import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { keyframes } from "@emotion/react";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { saveAs } from "file-saver";
import { listSkillAgentProjects } from "../../api/Skills/skillAgentProjectsApi";
import {
  createSkillAgentClientInfo,
  listSkillAgentClientInfos,
  uploadSkillAgentClientInfoItems,
} from "../../api/Skills/skillAgentClientInfosApi";
import { isPresentacionesRow } from "../Skills/skillAgentClientInfoKind";

const rise = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const softPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(173, 20, 87, 0.28); }
  50% { box-shadow: 0 0 0 10px rgba(173, 20, 87, 0); }
`;

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none !important",
  },
};

/**
 * Tras generar el PDF de muestras: elige proyecto Compás y guarda en PRESENTACIONES PDF.
 */
export default function MuestrasSavePdfToCompassDialog({
  open,
  onClose,
  blob = null,
  filename = "Muestras_SDS.pdf",
  suggestedTemporada = "",
  suggestedCustomerId = null,
  onSaved = null,
}) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [projects, setProjects] = useState([]);
  const [temporada, setTemporada] = useState("");
  const [projectId, setProjectId] = useState("");
  const [titulo, setTitulo] = useState("");
  const [alsoDownload, setAlsoDownload] = useState(true);

  useEffect(() => {
    if (!open) return;
    setError("");
    setTitulo(String(filename || "").replace(/\.pdf$/i, "") || "Presentación");
    setAlsoDownload(true);
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rows = await listSkillAgentProjects();
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setProjects(list);
        const seasons = [
          ...new Set(
            list.map((p) => String(p.Temporada || "").trim()).filter(Boolean),
          ),
        ];
        const preferSeason = String(suggestedTemporada || "").trim();
        const season =
          (preferSeason && seasons.includes(preferSeason)
            ? preferSeason
            : null) ||
          seasons[0] ||
          "";
        setTemporada(season);
        const inSeason = list.filter(
          (p) => String(p.Temporada || "").trim() === season,
        );
        const preferCid = Number(suggestedCustomerId);
        const match =
          inSeason.find(
            (p) =>
              Number.isFinite(preferCid) &&
              preferCid > 0 &&
              Number(p.CustomerId) === preferCid,
          ) || inSeason[0];
        setProjectId(match?.id ? String(match.id) : "");
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.error ||
              t("Samples.savePdfLoadProjectsError", {
                defaultValue: "No se pudieron cargar los proyectos de Compás.",
              }),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, filename, suggestedTemporada, suggestedCustomerId, t]);

  const seasons = useMemo(() => {
    return [
      ...new Set(
        projects.map((p) => String(p.Temporada || "").trim()).filter(Boolean),
      ),
    ].sort((a, b) => b.localeCompare(a, undefined, { numeric: true }));
  }, [projects]);

  const projectsInSeason = useMemo(() => {
    const season = String(temporada || "").trim();
    return projects
      .filter((p) => String(p.Temporada || "").trim() === season)
      .sort((a, b) =>
        String(a.CustomerName || a.Name || "").localeCompare(
          String(b.CustomerName || b.Name || ""),
          undefined,
          { sensitivity: "base" },
        ),
      );
  }, [projects, temporada]);

  useEffect(() => {
    if (!open || !projectsInSeason.length) return;
    const still = projectsInSeason.some(
      (p) => String(p.id) === String(projectId),
    );
    if (!still) setProjectId(String(projectsInSeason[0].id));
  }, [open, projectsInSeason, projectId]);

  const selectedProject = useMemo(
    () => projects.find((p) => String(p.id) === String(projectId)) || null,
    [projects, projectId],
  );

  const handleSave = async () => {
    if (!blob || !selectedProject || saving) return;
    const title = String(titulo || "").trim() || "Presentación";
    const pdfName = title.toLowerCase().endsWith(".pdf")
      ? title
      : `${title}.pdf`;
    setSaving(true);
    setError("");
    try {
      const existing = await listSkillAgentClientInfos({
        projectId: selectedProject.id,
      });
      const presentaciones = (Array.isArray(existing) ? existing : []).filter(
        isPresentacionesRow,
      );
      let dossier = presentaciones[0] || null;
      if (!dossier) {
        dossier = await createSkillAgentClientInfo({
          Titulo: selectedProject.CustomerName
            ? `Presentaciones PDF · ${selectedProject.CustomerName}`
            : "Presentaciones PDF",
          Kind: "presentaciones_pdf",
          CustomerId: selectedProject.CustomerId || null,
          CustomerName: selectedProject.CustomerName || null,
          Temporada: selectedProject.Temporada || temporada || null,
          ProjectId: selectedProject.id,
          OrgBrand: "STAGE",
        });
      }
      const file = new File([blob], pdfName, { type: "application/pdf" });
      await uploadSkillAgentClientInfoItems(dossier.id, [file]);
      if (alsoDownload) {
        try {
          saveAs(blob, pdfName);
        } catch {
          /* ignore local download */
        }
      }
      onSaved?.({
        dossierId: dossier.id,
        projectId: selectedProject.id,
        filename: pdfName,
      });
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.message ||
          t("Samples.savePdfError", {
            defaultValue: "No se pudo guardar el PDF en Compás.",
          }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose?.()}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          animation: `${rise} 0.38s ease-out`,
          ...motionSafe,
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2.25,
          pb: 1.5,
          background: `linear-gradient(135deg, ${alpha("#ad1457", 0.12)} 0%, ${alpha(
            "#0277bd",
            0.08,
          )} 100%)`,
          borderBottom: "1px solid",
          borderColor: alpha("#ad1457", 0.16),
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha("#ad1457", 0.14),
              color: "#ad1457",
              animation: `${softPulse} 2.4s ease-in-out infinite`,
              ...motionSafe,
            }}
          >
            <PictureAsPdfOutlinedIcon />
          </Box>
          <Box>
            <Typography
              variant="overline"
              sx={{ color: "#ad1457", letterSpacing: 1 }}
            >
              Compás
            </Typography>
            <DialogTitle sx={{ p: 0, fontSize: "1.15rem", fontWeight: 700 }}>
              {t("Samples.savePdfTitle", {
                defaultValue: "Guardar en PRESENTACIONES PDF",
              })}
            </DialogTitle>
          </Box>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("Samples.savePdfLead", {
            defaultValue:
              "Elige el proyecto. Si no existe la rama, se crea sola.",
          })}
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 2.5 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}
        {loading ? (
          <Box sx={{ py: 4, display: "grid", placeItems: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="save-pdf-season-label">
                {t("Samples.savePdfSeason", { defaultValue: "Temporada" })}
              </InputLabel>
              <Select
                labelId="save-pdf-season-label"
                label={t("Samples.savePdfSeason", {
                  defaultValue: "Temporada",
                })}
                value={temporada}
                onChange={(e) => setTemporada(e.target.value)}
                disabled={saving || !seasons.length}
              >
                {seasons.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="save-pdf-project-label">
                {t("Samples.savePdfProject", {
                  defaultValue: "Proyecto Compás",
                })}
              </InputLabel>
              <Select
                labelId="save-pdf-project-label"
                label={t("Samples.savePdfProject", {
                  defaultValue: "Proyecto Compás",
                })}
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                disabled={saving || !projectsInSeason.length}
              >
                {projectsInSeason.map((p) => (
                  <MenuItem key={p.id} value={String(p.id)}>
                    {p.CustomerName
                      ? `${p.CustomerName} · ${p.Name || `#${p.id}`}`
                      : p.Name || `Proyecto #${p.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              fullWidth
              label={t("Samples.savePdfFilename", {
                defaultValue: "Nombre del PDF",
              })}
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              disabled={saving}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={alsoDownload}
                  onChange={(e) => setAlsoDownload(e.target.checked)}
                  disabled={saving}
                />
              }
              label={t("Samples.savePdfAlsoDownload", {
                defaultValue: "Descargar también en este equipo",
              })}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button onClick={() => onClose?.()} disabled={saving}>
          {t("Samples.cancel", { defaultValue: "Cancelar" })}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading || !blob || !projectId}
          startIcon={
            saving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveOutlinedIcon />
            )
          }
          sx={{
            bgcolor: "#ad1457",
            "&:hover": { bgcolor: "#880e4f" },
          }}
        >
          {t("Samples.savePdfConfirm", { defaultValue: "Guardar en Compás" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
